import React, { useEffect, useState } from 'react';
import type { AnalyticsDashboard } from '@nordcrest/shared';
import * as api from '../../lib/api';
import { MetricCard } from './MetricCard';

export function DashboardOverview() {
  const [analytics, setAnalytics] = useState<AnalyticsDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const data = await api.getAnalytics();
      setAnalytics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Surveys"
          value={analytics.totalSurveys}
          color="blue"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          }
        />

        <MetricCard
          title="Completion Rate"
          value={`${analytics.completionRate}%`}
          color="green"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        <MetricCard
          title="Hot Leads"
          value={analytics.hotLeads}
          color="red"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
            </svg>
          }
        />

        <MetricCard
          title="Callback Requests"
          value={analytics.callbackRequests}
          color="yellow"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          }
        />
      </div>

      {/* Lead Temperature Distribution */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Lead Distribution</h2>
        <div className="grid grid-cols-3 gap-4">
          {analytics.leadsByTemperature.map((temp) => (
            <div
              key={temp.temperature}
              className={`p-4 rounded-lg ${
                temp.temperature === 'hot'
                  ? 'bg-red-50'
                  : temp.temperature === 'warm'
                  ? 'bg-yellow-50'
                  : 'bg-blue-50'
              }`}
            >
              <p className="text-sm font-medium text-gray-500 capitalize">{temp.temperature}</p>
              <p className="text-2xl font-bold text-gray-900">{temp.count}</p>
              <p className="text-sm text-gray-500">{temp.percentage}%</p>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6">
          <p className="text-sm font-medium text-gray-500">Total Leads</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{analytics.totalLeads}</p>
        </div>
        <div className="card p-6">
          <p className="text-sm font-medium text-gray-500">Avg. Lead Score</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{analytics.avgLeadScore}</p>
        </div>
        <div className="card p-6">
          <p className="text-sm font-medium text-gray-500">Completed Surveys</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{analytics.completedSurveys}</p>
        </div>
      </div>
    </div>
  );
}
